import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { accountQuery, cmsPageQuery, hostBaseUrl } from '@/api';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { EmptyState, Spinner, Toast } from '@/ui/common';
import { PromoDetailPanel } from '@/ui/promotions';

export const Route = createFileRoute('/promotions/$slug')({
  component: PromotionDetailsPage,
});

function PromotionDetailsPage() {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const { slug } = Route.useParams();
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const { data: account } = useQuery(accountQuery());
  const accountGuid = account?.Guid ?? '';

  const {
    data: page,
    isLoading,
    isError,
    error,
  } = useQuery(cmsPageQuery(accountGuid, slug, selectedLanguage));

  return (
    <div className="flex w-full flex-col px-4 pt-4 pb-6 lg:px-[200px] lg:pt-8 lg:pb-12">
      {isError ? (
        <Toast tone="danger">
          {label('Could not load promotion', 'Could not load promotion')}
          {error instanceof Error && error.message ? `: ${error.message}` : ''}
        </Toast>
      ) : null}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner label={label('Loading promotion', 'Loading promotion')} />
        </div>
      ) : page ? (
        <PromoDetailPanel
          page={page}
          usersBaseUrl={hostBaseUrl('users')}
          accountGuid={accountGuid}
          label={label}
        />
      ) : !isError ? (
        <div className="flex flex-col gap-4">
          <Link
            to="/promotions"
            className="w-fit text-body-m text-text-primary hover:text-text-secondary"
          >
            ← {label('AllPromotions', 'All promotions')}
          </Link>
          <EmptyState
            icon="promotions"
            title={label('Promotion not found', 'Promotion not found')}
            description={label(
              'This offer may have been removed.',
              'This offer may have been removed.',
            )}
          />
        </div>
      ) : null}
    </div>
  );
}
